import { holdoutDefaults } from '../../../lib/s2/defaults';
import { liveCaps, previewHoldout } from '../../../lib/s2/holdoutPreview';
import {
  HOLDOUT_ENTRY_KINDS,
  type HoldoutDef,
  type HoldoutEntryDef,
  type HoldoutWaveDef,
  type HoldoutWaveMixDef,
} from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import type { FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  ItemPicker,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  smallBtn,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';

type H = Required<HoldoutDef>;

/**
 * A holdout (holdout.html §5): where it runs, its set piece, the setup and breather clocks, entries, the wave table (mix, entries,
 * spawn interval, boss, crate), placement limits and squad scaling; then the preview: the stacked wave chart per squad size against
 * the live caps, total zombies, the kill bound and the minimum duration.
 */
export function HoldoutForm({ value, onChange, errors }: FormProps<HoldoutDef>) {
  const h = withDefaults(holdoutDefaults(), (value ?? {}) as H);
  const set = <K extends keyof H>(k: K, x: H[K]) => onChange({ ...h, [k]: x });
  const b = useContentBundle();
  const opts = (list: ({ id?: string; name?: string } | null)[] | null | undefined) =>
    (list ?? [])
      .filter((x) => x?.id)
      .map((x) => ({ value: x!.id!, label: `${x!.name || x!.id} (${x!.id})` }));
  const zombies = opts(b.zombies);
  const tables = (b.lootTables ?? []).map((t) => ({ value: t.id ?? '', label: t.id ?? '' }));
  const entryIds = (h.entries ?? []).map((e) => e.id ?? '').filter(Boolean);
  const limits = h.placementLimits ?? {};
  const scale = h.squadScale ?? [];
  return (
    <>
      <Fieldset legend="Holdout">
        <TextField label="Id" mono value={h.id} issues={errors.id} onChange={(x) => set('id', x)} />
        <TextField
          label="Name"
          value={h.name}
          issues={errors.name}
          onChange={(x) => set('name', x)}
        />
        <StringListEditor
          label="Categories"
          value={h.categories ?? []}
          errors={errors}
          base="categories"
          onChange={(x) => set('categories', x)}
        />
        <NumberField
          label="Min footprint (m²)"
          min={0}
          value={h.minFootprintM2}
          issues={errors.minFootprintM2}
          onChange={(x) => set('minFootprintM2', x ?? 0)}
        />
        <SelectField
          label="Set piece"
          value={h.setPiece}
          options={opts(b.setPieces)}
          issues={errors.setPiece}
          onChange={(x) => set('setPiece', x ?? '')}
        />
        <NumberField
          label="Setup (s)"
          integer
          min={0}
          value={h.setupS}
          issues={errors.setupS}
          onChange={(x) => set('setupS', x ?? 0)}
        />
        <NumberField
          label="Breather (s)"
          integer
          min={0}
          value={h.breatherS}
          issues={errors.breatherS}
          onChange={(x) => set('breatherS', x ?? 0)}
        />
      </Fieldset>
      <Fieldset legend="Entries">
        <ListEditor<HoldoutEntryDef>
          label="Entry points"
          items={h.entries ?? []}
          onChange={(x) => set('entries', x)}
          make={() => ({ id: '', kind: 'door', from: 1, telegraph: '' })}
          what="entry"
          issues={errors.entries}
          row={(e, i, setE) => (
            <div className="grid gap-2 sm:grid-cols-4">
              <TextField
                label="Id"
                mono
                value={e.id}
                issues={errors[`entries.${i}.id`]}
                onChange={(x) => setE({ ...e, id: x })}
              />
              <SelectField
                label="Kind"
                value={e.kind}
                options={HOLDOUT_ENTRY_KINDS.map((k) => ({ value: k, label: k }))}
                issues={errors[`entries.${i}.kind`]}
                onChange={(x) => setE({ ...e, kind: x ?? '' })}
              />
              <NumberField
                label="From wave"
                integer
                min={1}
                value={e.from}
                issues={errors[`entries.${i}.from`]}
                onChange={(x) => setE({ ...e, from: x ?? 1 })}
              />
              <TextField
                label="Telegraph"
                value={e.telegraph}
                issues={errors[`entries.${i}.telegraph`]}
                onChange={(x) => setE({ ...e, telegraph: x })}
              />
            </div>
          )}
        />
      </Fieldset>
      <Fieldset legend="Waves">
        <ListEditor<HoldoutWaveDef>
          label="Wave table"
          items={h.waves ?? []}
          onChange={(x) => set('waves', x)}
          make={() => ({ mix: [], entries: [], spawnIntervalS: 1, boss: '', crate: null })}
          what="wave"
          issues={errors.waves}
          row={(w, i, setW) => (
            <WaveEditor
              wave={w}
              index={i}
              zombies={zombies}
              tables={tables}
              entryIds={entryIds}
              errors={errors}
              onChange={setW}
            />
          )}
        />
      </Fieldset>
      <Fieldset legend="Placement limits and squad scale">
        <div className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase">Placement limits</span>
          {Object.entries(limits).map(([item, n]) => (
            <div key={item} className="mt-1 grid grid-cols-[minmax(0,1fr)_6rem_auto] gap-2">
              <span className="font-mono text-sm">{item}</span>
              <NumberInput
                label={`Limit ${item}`}
                integer
                min={0}
                value={n}
                onChange={(x) => set('placementLimits', { ...limits, [item]: x ?? 0 })}
              />
              <button
                type="button"
                aria-label={`Remove limit ${item}`}
                className={`${smallBtn} text-blam`}
                onClick={() => {
                  const next = { ...limits };
                  delete next[item];
                  set('placementLimits', next);
                }}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="mt-1 max-w-sm">
            <ItemPicker
              label="Add a placement limit"
              value=""
              none="(add a placeable)"
              onChange={(x) => x && !(x in limits) && set('placementLimits', { ...limits, [x]: 1 })}
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <CheckboxField
            label="Scale waves by squad size"
            hint="Wave counts × these factors for a squad of 1–4 (the page's 0.6 / 0.8 / 1.0 / 1.15). Off = 1 for every size."
            checked={scale.length > 0}
            onChange={(on) => set('squadScale', on ? [0.6, 0.8, 1, 1.15] : [])}
          />
          {scale.length > 0 && (
            <div className="mt-1 grid grid-cols-4 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Field key={i} label={`Squad ${i + 1}`} issues={errors[`squadScale.${i}`]}>
                  {(id) => (
                    <NumberInput
                      id={id}
                      step={0.05}
                      min={0}
                      value={scale[i] ?? 1}
                      onChange={(x) =>
                        set(
                          'squadScale',
                          [0, 1, 2, 3].map((j) => (j === i ? (x ?? 0) : (scale[j] ?? 1))),
                        )
                      }
                    />
                  )}
                </Field>
              ))}
            </div>
          )}
        </div>
      </Fieldset>
      <HoldoutPreviewPanel holdout={h} />
    </>
  );
}

function WaveEditor({
  wave: w,
  index: i,
  zombies,
  tables,
  entryIds,
  errors,
  onChange,
}: {
  wave: HoldoutWaveDef;
  index: number;
  zombies: { value: string; label: string }[];
  tables: { value: string; label: string }[];
  entryIds: string[];
  errors: FieldErrors;
  onChange: (w: HoldoutWaveDef) => void;
}) {
  const base = `waves.${i}`;
  const entries = w.entries ?? [];
  return (
    <div className="space-y-2">
      <ListEditor<HoldoutWaveMixDef>
        label={`Wave ${i + 1} mix`}
        items={w.mix ?? []}
        onChange={(x) => onChange({ ...w, mix: x })}
        make={() => ({ zombieId: zombies[0]?.value ?? '', count: 1 })}
        what="zombie"
        issues={errors[`${base}.mix`]}
        row={(m, j, setM) => (
          <div className="grid grid-cols-[minmax(0,1fr)_6rem] gap-2">
            <SelectInput
              label={`Wave ${i + 1} zombie ${j + 1}`}
              value={m.zombieId}
              options={zombies}
              onChange={(x) => setM({ ...m, zombieId: x ?? '' })}
            />
            <NumberInput
              label={`Wave ${i + 1} count ${j + 1}`}
              integer
              min={0}
              value={m.count}
              onChange={(x) => setM({ ...m, count: x ?? 0 })}
            />
          </div>
        )}
      />
      <div>
        <span className="text-xs font-semibold uppercase">Entries used</span>
        <div className="flex flex-wrap gap-3">
          {entryIds.length === 0 && (
            <span className="text-xs text-bruise">Add entry points first.</span>
          )}
          {entryIds.map((id) => (
            <label key={id} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={entries.includes(id)}
                onChange={(e) =>
                  onChange({
                    ...w,
                    entries: e.target.checked ? [...entries, id] : entries.filter((x) => x !== id),
                  })
                }
              />
              <span className="font-mono">{id}</span>
            </label>
          ))}
          {entries
            .filter((x) => !entryIds.includes(x))
            .map((x) => (
              <span key={x} className="font-mono text-sm text-blam">
                {x} (unknown)
              </span>
            ))}
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <NumberField
          label="Spawn interval (s)"
          min={0}
          step={0.1}
          value={w.spawnIntervalS}
          issues={errors[`${base}.spawnIntervalS`]}
          onChange={(x) => onChange({ ...w, spawnIntervalS: x ?? 0 })}
        />
        <SelectField
          label="Boss"
          none="(no boss)"
          value={w.boss}
          options={zombies}
          issues={errors[`${base}.boss`]}
          onChange={(x) => onChange({ ...w, boss: x ?? '' })}
        />
        <div>
          <CheckboxField
            label="Crate"
            checked={!!w.crate}
            onChange={(on) => onChange({ ...w, crate: on ? { lootTableId: '', rolls: 2 } : null })}
          />
          {w.crate && (
            <div className="mt-1 grid grid-cols-[minmax(0,1fr)_4rem] gap-1">
              <SelectInput
                label={`Wave ${i + 1} crate table`}
                value={w.crate.lootTableId}
                options={tables}
                onChange={(x) => onChange({ ...w, crate: { ...w.crate, lootTableId: x ?? '' } })}
              />
              <NumberInput
                label={`Wave ${i + 1} crate rolls`}
                integer
                min={0}
                value={w.crate.rolls}
                onChange={(x) => onChange({ ...w, crate: { ...w.crate, rolls: x ?? 0 } })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PALETTE = [
  '#4b5563',
  '#c0392b',
  '#2563eb',
  '#d97706',
  '#7c3aed',
  '#059669',
  '#db2777',
  '#0891b2',
];

/** Stacked bars per wave, one chart per squad size, with the live caps drawn across; then totals, kill bound and min duration. */
export function HoldoutPreviewPanel({ holdout }: { holdout: HoldoutDef }) {
  const b = useContentBundle();
  const caps = liveCaps(b.holdoutRules);
  const previews = [1, 2, 3, 4].map((n) => previewHoldout(holdout, n));
  const ids = [
    ...new Set(previews.flatMap((p) => p.waves.flatMap((w) => w.mix.map((m) => m.zombieId)))),
  ];
  const color = (id: string) => PALETTE[ids.indexOf(id) % PALETTE.length]!;
  const max = Math.max(caps.high, ...previews.flatMap((p) => p.waves.map((w) => w.total)), 1);
  const H = 120;
  const y = (n: number) => 8 + H - (n / max) * H;
  return (
    <section className="mb-4 rounded border-2 border-ink bg-white p-3" aria-label="Holdout preview">
      <h3 className="text-sm font-bold">Preview</h3>
      <p className="text-xs text-bruise">
        Wave totals never change with the cap: a lower tier streams the same wave thinner. The
        numbers are HoldoutRules' (kill bound = MaxKills, min = MinDurationS), as the server checks
        them.
      </p>
      <div className="mt-1 flex flex-wrap gap-3 text-xs">
        {ids.map((id) => (
          <span key={id} className="flex items-center gap-1">
            <span className="inline-block h-3 w-3" style={{ background: color(id) }} /> {id}
          </span>
        ))}
        <span>
          caps: low {caps.low} · mid {caps.mid} · high {caps.high}
        </span>
      </div>
      <div className="mt-2 grid gap-3 md:grid-cols-2">
        {previews.map((p) => {
          const n = Math.max(1, p.waves.length);
          const bw = Math.min(40, 200 / n);
          const W = 40 + n * (bw + 8);
          return (
            <figure key={p.squadSize} className="rounded border border-newsprint p-2">
              <figcaption className="text-xs font-semibold">
                Squad {p.squadSize} (×{p.scale}): {p.totalZombies} zombies · kill bound +
                {p.killBound} · min {p.minDurationS} s
              </figcaption>
              <svg
                viewBox={`0 0 ${W} ${H + 24}`}
                className="w-full"
                role="img"
                aria-label={`Waves for squad ${p.squadSize}`}
              >
                {p.waves.map((w, i) => {
                  let acc = 0;
                  const x = 36 + i * (bw + 8);
                  return (
                    <g key={i}>
                      {w.mix.map((m, j) => {
                        const top = y(acc + m.count);
                        const h = y(acc) - top;
                        acc += m.count;
                        return (
                          <rect
                            key={j}
                            x={x}
                            y={top}
                            width={bw}
                            height={h}
                            fill={color(m.zombieId)}
                          />
                        );
                      })}
                      <text x={x + bw / 2} y={H + 20} fontSize={9} textAnchor="middle">
                        {w.wave}
                        {w.boss ? '★' : ''}
                      </text>
                      <text x={x + bw / 2} y={y(w.total) - 2} fontSize={8} textAnchor="middle">
                        {w.total}
                      </text>
                    </g>
                  );
                })}
                {(['low', 'mid', 'high'] as const).map((k) => (
                  <g key={k}>
                    <line
                      x1={30}
                      x2={W}
                      y1={y(caps[k])}
                      y2={y(caps[k])}
                      stroke="#c0392b"
                      strokeDasharray="3 2"
                      strokeWidth={0.8}
                    />
                    <text x={2} y={y(caps[k]) + 3} fontSize={8}>
                      {k} {caps[k]}
                    </text>
                  </g>
                ))}
              </svg>
            </figure>
          );
        })}
      </div>
    </section>
  );
}
